import { useQuery } from '@tanstack/react-query'

import { lexicalHasContent } from '@/app/buy/seller/[sellerSlug]/content/cmsSellerProfileAbout'

import type { CmsTermsBody } from './cmsTerms'

export const CMS_SELLER_TERMS_PATH = '/home-portal/api/cms/seller-terms'

const STALE_TIME_MS = 5 * 60 * 1000

async function fetchCmsSellerTerms(
  sellerSlug: string,
  storeFrontId: string
): Promise<CmsTermsBody | null> {
  const params = new URLSearchParams({ sellerSlug, storeFrontId })
  const response = await fetch(`${CMS_SELLER_TERMS_PATH}?${params.toString()}`)
  if (!response.ok) {
    throw new Error(`CMS seller terms request failed: ${response.status}`)
  }
  const body: unknown = await response.json()
  return lexicalHasContent(body) ? (body as CmsTermsBody) : null
}

/**
 * The CMS accept-terms body for a seller, read through the `seller-terms` route handler because
 * the LDP is a Pages Router page and the CMS loader is `server-only`.
 *
 * Runs only when both ids exist and the caller says the buyer may need the accept-terms page.
 * `isSettled` is true once there is an answer either way, or when there is nothing to wait for;
 * an error settles to `null`, which is today's rendering, so the CMS can never block acceptance.
 */
export function useCmsSellerTerms({
  sellerSlug,
  storeFrontId,
  enabled,
}: {
  sellerSlug: string | null | undefined
  storeFrontId: string | null | undefined
  enabled: boolean
}): { data: CmsTermsBody | null; isSettled: boolean } {
  const ids = sellerSlug && storeFrontId ? { sellerSlug, storeFrontId } : null
  const isEnabled = enabled && ids !== null

  const query = useQuery({
    queryKey: ['cmsSellerTerms', sellerSlug, storeFrontId],
    queryFn: () =>
      ids
        ? fetchCmsSellerTerms(ids.sellerSlug, ids.storeFrontId)
        : Promise.resolve(null),
    enabled: isEnabled,
    retry: false,
    staleTime: STALE_TIME_MS,
  })

  return {
    data: query.data ?? null,
    isSettled: !isEnabled || query.isSuccess || query.isError,
  }
}
